import LogoComponent from "@/components/logo/LogoComponent";
import { classNames } from "@/helpers/common/classNames";
import { NavigationItem } from "@/types/longhouseTypes";
import { gql, useQuery } from "@apollo/client";
import { Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SidebarNavItem } from "./items/SidebarNavItem";

const SEARCH_PLAYERS_QUERY = gql`
    query SearchPlayers($searchQuery: String, $limit: Int) {
        searchPlayers(searchQuery: $searchQuery, limit: $limit) {
            id
            name
            nickname
        }
    }
`;

interface Player {
    id: string;
    name: string;
    nickname: string;
}

interface SidebarNavProps {
    navigation: NavigationItem[];
    collapsed: boolean;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ navigation, collapsed }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchRef = useRef<HTMLDivElement>(null);
    const [playerSearch, setPlayerSearch] = useState("");
    const [showResults, setShowResults] = useState(false);

    const { data: searchData, loading: searchLoading } = useQuery<{
        searchPlayers: Player[];
    }>(SEARCH_PLAYERS_QUERY, {
        variables: {
            searchQuery: playerSearch,
            limit: 10,
        },
        skip: playerSearch.length < 2,
    });

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target as Node)
            ) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlePlayerClick = (playerId: string) => {
        navigate(`/dashboard/players/${playerId}`);
        setPlayerSearch("");
        setShowResults(false);
    };

    return (
        <aside
            className={classNames(
                "hidden h-full flex-col border-r border-slate-200 bg-white transition-all duration-300 lg:flex dark:border-slate-700 dark:bg-slate-800",
                collapsed ? "w-20" : "w-72",
            )}
        >
            <div className="flex grow flex-col gap-y-5 overflow-y-auto pt-4 pb-4">
                <div className="flex h-16 shrink-0 items-center justify-center">
                    <Link to="/longhouse">
                        {collapsed ? (
                            <img
                                src="/vanderheim-icon.png"
                                alt="Vanderheim Logo"
                                className="h-12 transition-all duration-300"
                            />
                        ) : (
                            <LogoComponent
                                className={classNames(
                                    "h-12 transition-all duration-300",
                                )}
                            />
                        )}
                    </Link>
                </div>
                {!collapsed && (
                    <>
                        {/* Player Search */}
                        <div className="px-4 pb-4" ref={searchRef}>
                            <div className="relative">
                                <Search className="text-brand absolute top-2.5 left-3 h-4 w-4" />
                                <input
                                    type="text"
                                    placeholder="Search Villagers..."
                                    value={playerSearch}
                                    onChange={(e) => {
                                        setPlayerSearch(e.target.value);
                                        setShowResults(true);
                                    }}
                                    onFocus={() => setShowResults(true)}
                                    className="bg-brand_charcoal border-brand text-brand placeholder-brand/60 focus:border-brand focus:ring-brand w-full rounded-lg border py-2 pr-4 pl-9 text-sm focus:ring-1 focus:outline-none"
                                />
                            </div>

                            {/* Search Results Dropdown */}
                            {showResults && playerSearch.length >= 2 && (
                                <div className="bg-brand_charcoal border-brand absolute right-4 left-4 z-50 mt-1 rounded-lg border shadow-lg">
                                    {searchLoading && (
                                        <div className="text-brand p-4 text-center text-sm">
                                            Searching...
                                        </div>
                                    )}
                                    {!searchLoading &&
                                        searchData?.searchPlayers.length ===
                                            0 && (
                                            <div className="text-brand p-4 text-center text-sm">
                                                No players found
                                            </div>
                                        )}
                                    {!searchLoading &&
                                        searchData?.searchPlayers.map(
                                            (player) => (
                                                <button
                                                    key={player.id}
                                                    onClick={() =>
                                                        handlePlayerClick(
                                                            player.id,
                                                        )
                                                    }
                                                    className="border-brand hover:bg-brand/10 w-full border-b p-3 text-left text-sm last:border-b-0"
                                                >
                                                    <span className="text-brand font-semibold">
                                                        {player.name}
                                                    </span>
                                                    {player.nickname && (
                                                        <span className="text-brand/60 ml-2">
                                                            ({player.nickname})
                                                        </span>
                                                    )}
                                                </button>
                                            ),
                                        )}
                                </div>
                            )}
                        </div>

                        <nav className="flex flex-1 flex-col px-2 pt-6">
                            <ul className="flex flex-1 flex-col gap-y-2 rounded-md">
                                {navigation.map((item, index) => (
                                    <SidebarNavItem
                                        key={index}
                                        item={item}
                                        isActive={
                                            item.href === location.pathname
                                        }
                                        disabled={item.disabled}
                                    />
                                ))}
                            </ul>
                        </nav>
                    </>
                )}
            </div>
        </aside>
    );
};

export default SidebarNav;
